import { useCallback, useMemo, useState } from 'react'
import { trackFirebaseEvent } from '../../../../../../../firebase/firebase-analytics'
import { useProjectMenuStore } from '../../../../../../../zustand/project-menu/use-project-menu-store'
import { useHistory } from 'react-router-dom'
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { tableColumnSizeOptions } from '../../../../../../../components/stories/organisms/table/constants'
import { projectTreeItemTypeOptions } from '../../../../../../../react-query/project/query/use-get-project-tree-query'
import {
  buildColumnsDefault,
  buildColumnsSkeleton,
  buildData,
  buildDataSkeleton
} from '../table-options'
import { useMeQuery } from '../../../../../../../react-query/authentication/query/use-me-query'
import { serializeQueryString } from '../../../../../../../utils/urlManager'
import {
  myProjectSortDirectionOptions,
  myProjectSortOptions,
  useGetMyProjectsQuery
} from '../../../../../../../react-query/project/query/use-get-my-projects-query'
import { useIsDesktop } from '../../../../../../../zustand/browser-store/use-browser-store'
import { useProjectMenuListViewControl } from '../../../../../../../hooks/use-project-menu-list-view-control'

const emptyUserPermissions = []

const PENDING_COUNTS_REFETCH_INTERVAL_MS = 30000

export const useMyProjectsTable = ({ formatMessage, onLeaveProject }) => {
  const history = useHistory()
  const { data: user } = useMeQuery()
  const isDesktop = useIsDesktop()

  const { viewType, tab } = useProjectMenuListViewControl()

  const userPermissions = useMemo(
    () => user?.permissions ?? emptyUserPermissions,
    [user?.permissions]
  )
  const currentOrganizationId = user?.organisationId ?? null
  const hasPaidLicense = Boolean(user?.license?.paid)
  const canCreateProjects = userPermissions.includes('CreateProjects')

  const [pageNumber, setPageNumber] = useState(0)

  const pageSize = useProjectMenuStore((state) => state.projectListPageSize)
  const setPageSize = useProjectMenuStore(
    (state) => state.setProjectListPageSize
  )

  const search = useProjectMenuStore((state) => state.projectMenuSearch)
  const setSearch = useProjectMenuStore((state) => state.setProjectMenuSearch)

  const sorting = useProjectMenuStore(
    (state) =>
      state.myProjectsSorting ?? [
        { id: myProjectSortOptions.LAST_MODIFIED, desc: true }
      ]
  )

  const setSorting = useProjectMenuStore((state) => state.setMyProjectsSorting)

  const [
    { id: projectSort = myProjectSortOptions.LAST_MODIFIED, desc = true } = {}
  ] = sorting

  const sortDirection = desc
    ? myProjectSortDirectionOptions.DESC
    : myProjectSortDirectionOptions.ASC

  const queryParams = useMemo(
    () => ({
      page: pageNumber,
      size: pageSize,
      projectSort,
      sortDirection,
      search
    }),
    [pageNumber, pageSize, projectSort, search, sortDirection]
  )

  const { data, isError, isLoading } = useGetMyProjectsQuery({
    params: queryParams,
    options: {
      // sc-57356: the backend returns -1 for openPins/openTasks when the
      // counts are not cached yet and warms the cache in a background job.
      // While any count on the current page is -1, poll every 30s until the
      // real values arrive; otherwise disable polling.
      refetchInterval: (query) => {
        const content = query.state.data?.pages?.[0]?.content ?? []
        const hasPendingCounts = content.some(
          (item) =>
            (item.openPins ?? item.openPinCount) === -1 ||
            (item.openTasks ?? item.openTaskCount) === -1
        )

        return hasPendingCounts ? PENDING_COUNTS_REFETCH_INTERVAL_MS : false
      }
    }
  })

  const currentPage = data?.pages?.[0]
  const tableData = useMemo(
    () =>
      buildData(currentPage?.content ?? [], {
        canCreateProjects,
        currentOrganizationId,
        hasPaidLicense
      }),
    [canCreateProjects, currentPage, currentOrganizationId, hasPaidLicense]
  )

  const handleOpenProject = useCallback(
    (projectId) => {
      history.push(`/project/${projectId}`)
    },
    [history]
  )

  const handleOpenProjectSettings = useCallback(
    (projectId) => {
      history.push(`/project/settings/${projectId}?view=${viewType}&tab=${tab}`)
    },
    [history, viewType, tab]
  )

  const handleOpenProjectInfoPanel = useCallback(
    (projectId) => {
      history.push(`/project/info/${projectId}?view=${viewType}&tab=${tab}`)
    },
    [history, viewType, tab]
  )

  const handleCopyProject = useCallback(
    (item) => {
      if (item.type !== projectTreeItemTypeOptions.PROJECT) return

      const queryString = item.parentId
        ? serializeQueryString({ folderId: item.parentId })
        : ''

      history.push(
        `/project/${item.id}/copy${queryString}?view=${viewType}&tab=${tab}`
      )
    },
    [history, viewType, tab]
  )

  const resetPage = useCallback(() => {
    setPageNumber(0)
  }, [])

  const handlePageChange = useCallback((nextPageNumber) => {
    setPageNumber(Math.max(nextPageNumber, 0))
  }, [])

  const handlePageSizeChange = useCallback(
    (nextPageSize) => {
      setPageSize(nextPageSize)
      resetPage()
    },
    [resetPage]
  )

  const handleSearchChange = useCallback(
    (nextSearch) => {
      setSearch(nextSearch)
      resetPage()
    },
    [resetPage]
  )

  const handleSortingChange = useCallback(
    (updater) => {
      const nextSorting = updater(sorting)

      if (nextSorting[0]?.id !== sorting[0]?.id) {
        trackFirebaseEvent('my_projects_sort_by_changed', {
          source: 'project_list',
          extra: nextSorting[0]?.id
        })
      }

      if (nextSorting[0]?.desc !== sorting[0]?.desc) {
        trackFirebaseEvent('my_projects_sort_direction_changed', {
          source: 'project_list',
          extra: nextSorting[0]?.desc
        })
      }

      setSorting(nextSorting)
      setPageNumber(0)
    },
    [sorting, setSorting]
  )

  const columnsDefault = useMemo(
    () =>
      buildColumnsDefault({
        formatMessage,
        onCopyProject: handleCopyProject,
        onLeaveProject,
        onOpenProject: handleOpenProject,
        onOpenProjectSettings: handleOpenProjectSettings,
        onOpenProjectPanel: handleOpenProjectInfoPanel,
        isDesktop
      }),
    [
      formatMessage,
      handleCopyProject,
      handleOpenProject,
      handleOpenProjectSettings,
      onLeaveProject,
      handleOpenProjectInfoPanel,
      isDesktop
    ]
  )

  const columnsSkeleton = useMemo(
    () =>
      buildColumnsSkeleton({
        formatMessage
      }),
    [formatMessage]
  )

  const isTableSkeleton = isLoading
  const tableColumns = isTableSkeleton ? columnsSkeleton : columnsDefault

  const tableDataVariant = useMemo(
    () => (isTableSkeleton ? buildDataSkeleton(pageSize) : tableData),
    [isTableSkeleton, pageSize, tableData]
  )

  const table = useReactTable({
    data: tableDataVariant,
    columns: tableColumns,
    state: {
      sorting,
      columnVisibility: {
        'open-pins': isDesktop,
        'open-tasks': isDesktop,
        projectSettings: isDesktop,
        projectPanel: isDesktop
      }
    },
    defaultColumn: {
      minSize: tableColumnSizeOptions.XXS
    },
    enableMultiSort: false,
    enableSortingRemoval: false,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id
  })

  const pagination = useMemo(
    () => ({
      pageNumber: currentPage?.number ?? pageNumber,
      pageSize: currentPage?.size ?? pageSize,
      totalElements: currentPage?.totalElements ?? 0,
      totalPages: currentPage?.totalPages ?? 0,
      onPageChange: handlePageChange
    }),
    [currentPage, handlePageChange, pageNumber, pageSize]
  )

  return {
    canCreateProjects,
    isError,
    pagination,
    pageSize,
    search,
    setPageSize: handlePageSizeChange,
    setSearch: handleSearchChange,
    table
  }
}
